import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2, Phone, ShieldCheck } from 'lucide-react';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@tark/shared/components';
import { useAuth } from '@tark/shared/hooks';
import {
  clearPendingAccountRecovery,
  getIdentityRecoveryError,
  getPendingAccountRecovery,
  normalizeUsPhone,
  requestWebPhoneChange,
  verifyWebPhoneChange,
} from '@tark/shared/lib';

function maskPhone(value?: string | null) {
  const digits = value?.replace(/\D/g, '') || '';
  return digits.length >= 4 ? `••• ••• ${digits.slice(-4)}` : 'Verified phone';
}

export default function ChangePhonePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const pendingRecovery = useMemo(() => getPendingAccountRecovery(), []);
  const isRecovery = searchParams.get('recovery') === '1' && Boolean(pendingRecovery);
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [newPhoneNumber, setNewPhoneNumber] = useState(pendingRecovery?.phoneNumber || '');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fullyEstablished = Boolean(
    user?.onboardingComplete &&
    user.emailVerified &&
    user.mobileVerified &&
    user.email?.trim() &&
    user.phone?.trim()
  );
  const normalizedPhone = normalizeUsPhone(newPhoneNumber);

  const handleRequest = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setNotice('');

    if (!normalizedPhone) {
      setError('Enter a valid mobile number.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await requestWebPhoneChange(normalizedPhone);
      if (!response.success) throw new Error(response.error || 'The code could not be sent.');
      setNotice('Verification code sent by SMS.');
      setStep('otp');
    } catch (requestError) {
      setError(getIdentityRecoveryError(requestError, 'We could not send a code right now. Try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    if (!normalizedPhone || verificationCode.length !== 6) {
      setError('Enter the 6-digit verification code.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await verifyWebPhoneChange(normalizedPhone, verificationCode);
      if (!response.success) throw new Error(response.error || 'The phone number could not be updated.');
      clearPendingAccountRecovery();
      window.location.replace('/login?reason=identity_changed');
    } catch (verifyError) {
      setError(getIdentityRecoveryError(verifyError, 'That code could not be verified. Try again.'));
      setSubmitting(false);
    }
  };

  if (!fullyEstablished) {
    return (
      <div className="mx-auto max-w-2xl space-y-5">
        <button
          type="button"
          onClick={() => navigate('/profile')}
          className="inline-flex min-h-10 items-center gap-2 text-sm font-semibold text-text-muted hover:text-text"
        >
          <ArrowLeft className="h-4 w-4" /> Back to profile
        </button>
        <Card variant="default">
          <CardContent className="p-6 text-center">
            <ShieldCheck className="mx-auto h-10 w-10 text-primary" />
            <h1 className="mt-4 text-xl font-semibold text-text">Finish securing your account first</h1>
            <p className="mt-2 text-sm leading-6 text-text-muted">
              A verified email, verified phone, and completed profile are required before a login number can be changed.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <button
        type="button"
        onClick={() => navigate('/profile')}
        className="inline-flex min-h-10 items-center gap-2 text-sm font-semibold text-text-muted hover:text-text"
      >
        <ArrowLeft className="h-4 w-4" /> Back to profile
      </button>

      <div>
        <h1 className="text-2xl font-semibold text-text">Change phone</h1>
        <p className="mt-1 text-sm text-text-muted">
          {isRecovery ? 'Verify the new number to finish recovering your existing account.' : 'Your current number is'}
          {!isRecovery ? <span className="font-semibold text-text"> {maskPhone(user?.phone)}</span> : null}
        </p>
      </div>

      <Card variant="default">
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <CardTitle>{step === 'phone' ? 'New phone number' : 'Verify new number'}</CardTitle>
            <span className="text-xs font-semibold text-text-muted">Step {step === 'phone' ? '1' : '2'} of 2</span>
          </div>
        </CardHeader>
        <CardContent>
          {step === 'phone' ? (
            <form onSubmit={handleRequest} className="space-y-5">
              <Input
                label="New phone number"
                type="tel"
                placeholder="(555) 123-4567"
                value={newPhoneNumber}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setNewPhoneNumber(event.target.value)}
                leftIcon={<Phone className="h-4 w-4" />}
                autoComplete="tel"
                autoFocus
                size="lg"
                className="bg-surface text-text"
              />
              {error ? <ErrorMessage message={error} /> : null}
              <Button
                type="submit"
                size="lg"
                className="w-full rounded-lg bg-text text-white hover:bg-text/90 active:bg-text/90"
                disabled={!normalizedPhone}
                isLoading={submitting}
                rightIcon={<ArrowRight className="h-4 w-4" />}
              >
                Send verification code
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerify} className="space-y-5">
              {notice ? (
                <div className="flex items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-primary">
                  <CheckCircle2 className="h-4 w-4 shrink-0" /> {notice}
                </div>
              ) : null}
              <p className="text-sm text-text-muted">
                Enter the code sent to <span className="font-semibold text-text">{newPhoneNumber}</span>.
              </p>
              <Input
                label="Verification code"
                type="text"
                inputMode="numeric"
                value={verificationCode}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setVerificationCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                autoComplete="one-time-code"
                autoFocus
                size="lg"
                className="bg-surface text-text"
              />
              {error ? <ErrorMessage message={error} /> : null}
              <Button
                type="submit"
                size="lg"
                className="w-full rounded-lg bg-text text-white hover:bg-text/90 active:bg-text/90"
                disabled={verificationCode.length !== 6}
                isLoading={submitting}
                rightIcon={<ArrowRight className="h-4 w-4" />}
              >
                Verify and update phone
              </Button>
              <button
                type="button"
                onClick={() => {
                  setStep('phone');
                  setVerificationCode('');
                  setError('');
                  setNotice('');
                }}
                className="w-full text-sm font-semibold text-text-muted hover:text-text"
              >
                Change the number
              </button>
            </form>
          )}
        </CardContent>
      </Card>

      <p className="text-center text-xs leading-5 text-text-muted">
        After the update, TARK signs out existing sessions and asks you to sign in with the new number.
      </p>
    </div>
  );
}

function ErrorMessage({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      {message}
    </div>
  );
}